import { Component, Input, Output, EventEmitter, OnDestroy, OnInit, ViewChild, OnChanges, SimpleChanges, HostListener, NgZone, ChangeDetectorRef } from '@angular/core';
import { DocumentEditorContainerComponent } from '@syncfusion/ej2-angular-documenteditor';
import { registerLicense } from '@syncfusion/ej2-base';
import { environment } from 'src/environments/environment';
import { MatSnackBar } from '@angular/material/snack-bar';
import { debounceTime, Subject, takeUntil } from 'rxjs';

registerLicense(environment.syncfusionKey)

const DEFAULT_AUTO_SAVE_TIME = 60000 // ms
const ERROR_STATUS_RESET_TIME = 5000 // ms

export interface DocumentSaveEvent {
  blob: Blob;
  documentName: string;
  isAutoSave: boolean;
}

type LoadErrorType = 'not_found' | 'conversion_failed' | 'network_error' | 'invalid_format' | 'unknown';
type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface LoadError {
  hasError: boolean;
  type: LoadErrorType;
  message: string;
  details?: string;
}

@Component({
  selector: 'app-doc-processor',
  templateUrl: './doc-processor.component.html',
  styleUrls: ['./doc-processor.component.scss']
})
export class DocProcessorComponent implements OnInit, OnDestroy, OnChanges {
  @Input() documentId?: string;
  @Input({required: true}) documentName: string = '';
  @Input({required: true}) documentContent: string = '';
  @Input() enableAutoSave: boolean = true;
  @Input() autoSaveInterval: number = DEFAULT_AUTO_SAVE_TIME;
  @Input() enableToolbar: boolean = true;
  @Input() height: string = '850px';
  @Input() showHeader: boolean = true;
  @Input() showCloseButton: boolean = true;
  @Input() showSaveButton: boolean = true;
  @Input() showPublishButton: boolean = false;
  @Input() publishButtonEnabled: boolean = false;
  @Input() pauseAutoSave: boolean = false;
  @Input() importUrl: string = '';

  @Output() documentChanged = new EventEmitter<any>();
  @Output() documentSaved = new EventEmitter<DocumentSaveEvent>();
  @Output() contentChanged = new EventEmitter<void>();
  @Output() closeEditor = new EventEmitter<void>();
  @Output() documentLoaded = new EventEmitter<void>();
  @Output() publishClicked = new EventEmitter<void>();

  @ViewChild('documenteditor_default')
  public container!: DocumentEditorContainerComponent;

  private saveSubject = new Subject<void>();
  private destroy$ = new Subject<void>();
  hasContentChanged: boolean = false;
  private saveStatusTimeout: any;
  private autoSaveIndicatorTimeout: any;

  public isLoadingDocument: boolean = true;
  public loadError: LoadError = { hasError: false, type: 'unknown', message: '' };
  public saveStatus: SaveStatus = 'idle';
  public autoSaveIndicator: 'idle' | 'saving' | 'saved' = 'idle';

  constructor(private snackBar: MatSnackBar, private ngZone: NgZone, private cdr: ChangeDetectorRef){}

  ngOnInit(): void {
    if (this.enableAutoSave) {
      this.setupAutoSave();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['documentContent'] && !changes['documentContent'].firstChange) {
      if (this.container?.documentEditor) {
        this.isLoadingDocument = true;
        this.loadDocument();
      }
    }
  }

  private showSnackbar(message: string, panelClass: string, duration: number = 3000): void {
    this.snackBar.open(message, 'Close', { duration, panelClass: [panelClass] });
  }

  private handleError(message: string): void {
    this.showSnackbar(message, 'error-snackbar', 5000);
  }

  public async saveDocument(): Promise<DocumentSaveEvent | null> {
    return this.performSave();
  }

  private setAutoSaveIndicator(state: 'idle' | 'saving' | 'saved'): void {
    this.ngZone.run(() => {
      this.autoSaveIndicator = state;
      this.renderAutoSaveIndicator();
    });
  }

  private async autoSaveDocument(): Promise<void> {
    this.setAutoSaveIndicator('saving');
    await this.performSave(true);
  }

  private async performSave(isAutoSave = false): Promise<DocumentSaveEvent | null> {
    try {
      this.setSaveStatus('saving');

      const blob = await this.container.documentEditor.saveAsBlob('Docx');

      const saveEvent: DocumentSaveEvent = {
        blob,
        documentName: this.documentName,
        isAutoSave
      };

      this.ngZone.run(() => {
        this.documentSaved.emit(saveEvent);
        this.setSaveStatus('idle');
        if (!isAutoSave) this.hasContentChanged = false;
      });

      if (isAutoSave) {
        this.setAutoSaveIndicator('saved');
        if (this.autoSaveIndicatorTimeout) clearTimeout(this.autoSaveIndicatorTimeout);
        this.autoSaveIndicatorTimeout = setTimeout(() => {
          this.setAutoSaveIndicator('idle');
        }, 2500);
      }

      return saveEvent;
    } catch (err) {
      this.ngZone.run(() => this.setSaveStatus('error', ERROR_STATUS_RESET_TIME));
      if (isAutoSave) this.setAutoSaveIndicator('idle');
      return null;
    }
  }

  private setSaveStatus(status: SaveStatus, resetAfter?: number): void {
    this.saveStatus = status;
    this.clearSaveStatusTimeout();

    if (resetAfter) {
      this.saveStatusTimeout = setTimeout(() => {
        this.saveStatus = 'idle';
      }, resetAfter);
    }
  }

  private clearSaveStatusTimeout(): void {
    if (this.saveStatusTimeout) {
      clearTimeout(this.saveStatusTimeout);
      this.saveStatusTimeout = null;
    }
  }

  private setupAutoSave(): void {
    this.saveSubject.pipe(
      debounceTime(this.autoSaveInterval),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      if (this.hasContentChanged && !this.pauseAutoSave) {
        this.autoSaveDocument();
        this.hasContentChanged = false;
      }
    });
  }

  onCreate(): void {
    setTimeout(() => {
      const container = this.container as any;
      if (container?.ribbon) {
        container.ribbon.showTab('Developer', false);
      }
    }, 100);

    this.loadDocument();
  }

  private renderAutoSaveIndicator(): void {
    this.cdr.detectChanges();
  }

  private async loadDocument(): Promise<void> {
    try {
      this.loadError.hasError = false;

      if (this.isUrl(this.documentContent)) {
        await this.loadFromUrl(this.documentContent);
      } else if (this.documentContent.startsWith('/path/')) {
        await this.loadMockDocument();
      } else if (this.documentContent.startsWith('{')) {
        await this.loadFromSfdt(this.documentContent);
      } else {
        await this.container.documentEditor.openBlank();
      }

      if (!this.loadError.hasError) {
        this.container.documentEditor.documentName = this.documentName;
        setTimeout(() => {
          this.isLoadingDocument = false;
          this.documentLoaded.emit();
          setTimeout(() => this.container?.documentEditor?.resize(), 50);
        }, 300);
      }
    } catch (error: any) {
      this.handleLoadError(error);
    }
  }

  private isUrl(content: string): boolean {
    return content.startsWith('http://') ||
           content.startsWith('https://') ||
           content.startsWith('blob:') ||
           content.startsWith('assets/');
  }

  private async loadFromUrl(url: string): Promise<void> {
    const response = await fetch(url);

    if (!response.ok) {
      this.handleHttpError(response);
      return;
    }

    const contentType = response.headers.get('content-type');

    if (contentType?.includes('application/json')) {
      await this.loadSfdtFromResponse(response);
    } else {
      await this.loadDocxFromResponse(response);
    }
  }

  private handleHttpError(response: Response): void {
    const errorMap: Record<number, [LoadErrorType, string, string]> = {
      404: ['not_found', 'Document not found', `The document "${this.documentName}" could not be found.`],
      403: ['network_error', 'Access denied', 'You do not have permission to access this document.'],
    };

    const [type, message, details] = errorMap[response.status] ||
      (response.status >= 500
        ? ['network_error', 'Server error', `Server error (${response.status}). Please try again later.`]
        : ['network_error', 'Failed to load document', `HTTP error: ${response.status} ${response.statusText}`]) as [LoadErrorType, string, string];

    this.setLoadError(type, message, details);
  }

  private async loadSfdtFromResponse(response: Response): Promise<void> {
    try {
      const sfdtData = await response.json();
      await this.container.documentEditor.open(JSON.stringify(sfdtData));
    } catch (error) {
      this.setLoadError('invalid_format', 'Invalid document format', 'The document appears to be corrupted or in an invalid SFDT format.');
    }
  }

  private async loadDocxFromResponse(response: Response): Promise<void> {
    try {
      const blob = await response.blob();

      if (blob.size === 0) {
        this.setLoadError('invalid_format', 'Empty document', 'The document file is empty.');
        return;
      }

      if (this.importUrl) {
        const formData = new FormData();
        formData.append('files', blob, 'document.docx');
        const importResponse = await fetch(this.importUrl, {
          method: 'POST',
          body: formData,
          credentials: 'include'
        });
        if (!importResponse.ok) {
          throw new Error(`Import failed: ${importResponse.status} ${importResponse.statusText}`);
        }
        const sfdt = await importResponse.text();
        await this.container.documentEditor.open(sfdt);
      } else {
        await this.container.documentEditor.open(blob);
      }
    } catch (error: any) {
      const message = error.message?.includes('conversion')
        ? 'Document conversion failed'
        : 'Failed to open document';
      const details = error.message?.includes('conversion')
        ? 'Unable to convert the DOCX file. The file may be corrupted.'
        : 'The document could not be opened. Please ensure it is a valid Word document.';

      this.setLoadError('conversion_failed', message, details);
    }
  }

  private async loadMockDocument(): Promise<void> {
    const documentIdMap: Record<string, string> = {
      'doc-008': 'assets/mock-documents/access-authorization-form-sfdt.json',
    };

    const actualPath = this.documentId ? documentIdMap[this.documentId] : undefined;

    if (!actualPath) {
      this.setLoadError('not_found', 'Document mapping not found', `No document mapping found for ID: ${this.documentId}`);
      return;
    }

    const response = await fetch(actualPath);

    if (!response.ok) {
      this.setLoadError('not_found', 'Document not found', `Mock document "${actualPath}" not found.`);
      return;
    }

    const sfdtData = await response.json();
    await this.container.documentEditor.open(JSON.stringify(sfdtData));
  }

  private async loadFromSfdt(sfdt: string): Promise<void> {
    try {
      await this.container.documentEditor.open(sfdt);
    } catch (error) {
      this.setLoadError('invalid_format', 'Invalid SFDT format', 'The document data is not in a valid SFDT format.');
    }
  }

  private handleLoadError(error: any): void {
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      this.setLoadError('network_error', 'Network error', 'Unable to connect to the server. Please check your internet connection.');
    } else {
      this.setLoadError('unknown', 'Unexpected error', `An unexpected error occurred: ${error.message || 'Unknown error'}`);
    }
  }

  private setLoadError(type: LoadErrorType, message: string, details: string): void {
    this.loadError = { hasError: true, type, message, details };
    this.isLoadingDocument = false;
    this.handleError(`${message}: ${details}`);
    setTimeout(() => this.closeEditor.emit(), 500);
  }

  onDocumentChange(event: any) {
    this.documentChanged.emit(event);
  }

  onContentChange() {
    this.hasContentChanged = true;
    this.contentChanged.emit();

    if (this.enableAutoSave) {
      this.saveSubject.next();
    }
  }

  onCloseEditor(): void {
    this.closeEditor.emit();
  }

  @HostListener('document:keydown.control.s', ['$event'])
  @HostListener('document:keydown.meta.s', ['$event'])
  handleKeyboardSave(event: Event): void {
    event.preventDefault();
    this.saveDocument();
  }

  ngOnDestroy(): void {
    this.clearSaveStatusTimeout();
    if (this.autoSaveIndicatorTimeout) clearTimeout(this.autoSaveIndicatorTimeout);
    this.destroy$.next();
    this.destroy$.complete();
  }
}
